import dayjs from "utils/dayjs";
import { LAST_30_DAYS, LAST_3_MONTHS, LAST_7_DAYS, LAST_MONTH, LAST_YEAR_1M, THIS_YEAR_1M, THIS_YEAR_1W, TODAY, YESTERDAY } from "./AnalyticsConstants";
import { Metric, MetricsData } from "./analytics";

export const calculateDifferences = (currentResponse: MetricsData, previousResponse: MetricsData): MetricsData => {
    if (previousResponse?.data?.length === 0) return currentResponse;
    for (let i = 0; i < currentResponse.data.length; i++) {
        const currentApp = currentResponse.data[i];
        const previousApp = previousResponse.data[i];
        // Percentage calculation  = (current_period - previos_priod) *100  / current_period_value
        for (let j = 0; j < currentApp.metrics.length; j++) {
            const currentMetric: Metric = currentApp.metrics[j];
            const previousMetric: Metric = previousApp.metrics[j] ?? null;
            if (previousMetric && currentMetric) {
                if (currentMetric.value !== null && previousMetric.value !== null) {
                    const difference = currentMetric.value - previousMetric.value;
                    const percentageChange =
                        ((currentMetric.value - previousMetric.value) * 100 / currentMetric.value);
                    currentMetric['difference'] = difference;
                    currentMetric['percentage_change'] = parseFloat(percentageChange.toFixed(3));
                }
            } else {
                currentMetric['difference'] = 0.0;
                currentMetric['percentage_change'] = 0.0;
            }
        }
    }
    return currentResponse;
}

export const formatDateWithMoment = (date: dayjs.Dayjs): string => {
    const year = date.year();
    const month = String(date.month() + 1).padStart(2, '0');
    const day = String(date.date()).padStart(2, '0');
    return `${year}-${month}-${day} 00:00:00`;
}

export const formatDate = (date: Date, timezone: string): string => {
    const momentDate = dayjs(date).tz(timezone);
    const year = momentDate.year();
    const month = String(momentDate.month() + 1).padStart(2, '0');
    const day = String(momentDate.date()).padStart(2, '0');
    return `${year}-${month}-${day} 00:00:00`;
}

export const upperBoundDateformat = (date: Date, timezone: string): string => {
    const momentDate = dayjs(date).tz(timezone);
    const nextDay = momentDate.clone().add(1, 'day');
    const year = nextDay.year();
    const month = String(nextDay.month() + 1).padStart(2, '0');
    const day = String(nextDay.date()).padStart(2, '0');
    const hours = '00';
    const minutes = '00';
    const seconds = '00';
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export const lowerBoundDateFormat = (date: Date, timezone: string): string => {
    const momentDate = dayjs(date).tz(timezone);
    const previousDay = momentDate.clone().subtract(1, 'day');
    const year = previousDay.year();
    const month = String(previousDay.month() + 1).padStart(2, '0');
    const day = String(previousDay.date()).padStart(2, '0');
    const hours = '00';
    const minutes = '00';
    const seconds = '00';
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export const formatDateRange = (start: Date, end: Date, dateRange: string, timezone: string): string => {
    const startMoment = dayjs.tz(start, timezone);
    const endMoment = dayjs.tz(end, timezone);

    const startMonth = startMoment.format('MMM');
    const endMonth = endMoment.format('MMM');
    
    if (dateRange === LAST_7_DAYS) {
        const adjustedStartMoment = startMoment.clone().subtract(1, 'day');
        const adjustedEndMoment = endMoment.clone().subtract(1, 'day');

        const startMonth = adjustedStartMoment.format('MMM');
        const endMonth = adjustedEndMoment.format('MMM');
        const formattedStartDay = adjustedStartMoment.date();
        const formattedEndDay = adjustedEndMoment.date();
        
        if (startMonth === endMonth) {
            return `${startMonth} ${formattedStartDay} - ${formattedEndDay}`;
        } else {
            return `${startMonth} ${formattedStartDay} - ${endMonth} ${formattedEndDay}`;
        }
        } else if (dateRange === LAST_30_DAYS) {
            const actualEndMoment = endMoment.clone().subtract(1, 'day');
            const formattedStartDay = startMoment.date();
            const formattedEndDay = actualEndMoment.date();
            const actualEndMonth = actualEndMoment.format('MMM');
            
            return `${startMonth} ${formattedStartDay} - ${actualEndMonth} ${formattedEndDay}`;
    } else {
        return `${startMoment.date()} ${startMonth} - ${endMoment.date()} ${endMonth}`;
    }
}

export function getTodaysDateWithTimezone(timezone: string) {
    const date: Date = new Date()
    const currentDate = dayjs(date).tz(timezone);
    return `${currentDate.format('MMMM')} ${currentDate.date()}`;
}

export const getDateRangeBasedTimeGrain = (dateRange: string, timezone: string): string[] => {
    const today = dayjs().tz(timezone);
    let formattedDateRange: string[] = [];

    switch (dateRange) {
        case TODAY: {
            const currentToday = today.clone().toDate();
            const tomo = today.clone().add(1, 'day');
            formattedDateRange = [formatDate(currentToday, timezone), formatDateWithMoment(tomo)];
            break;
        }
        case YESTERDAY: {
            const currentYesterday = dayjs().tz(timezone);
            const yesterday = currentYesterday.clone().subtract(1, 'day');
            formattedDateRange = [formatDate(yesterday.toDate(), timezone), formatDate(currentYesterday.toDate(), timezone)];
            break;
        }
        case LAST_7_DAYS: {
            const startDate7 = today.clone().subtract(7, 'days').toDate();
            const endDate7 = today.toDate();
            formattedDateRange = [formatDate(startDate7, timezone), formatDate(endDate7, timezone)];
            break;
        }
        case LAST_30_DAYS: {
            const startDate30 = today.clone().subtract(29, 'days').toDate();
            const endDate30 = today.toDate();
            formattedDateRange = [lowerBoundDateFormat(startDate30, timezone), formatDate(endDate30, timezone)];
            break;
        }
        case LAST_MONTH: {
            const startOfLastMonth = today.clone().subtract(1, 'month').startOf('month').toDate();
            const endOfLastMonth = today.clone().subtract(1, 'month').endOf('month').toDate();
            formattedDateRange = [formatDate(startOfLastMonth, timezone),upperBoundDateformat(endOfLastMonth, timezone)];
            break;
        }
        case LAST_3_MONTHS: {
            const startOfLast3Months = today.clone().subtract(3, 'months').startOf('month').toDate();
            const endOfLast3Months = today.clone().subtract(1, 'months').endOf('month').toDate();
            formattedDateRange = [formatDate(startOfLast3Months, timezone), upperBoundDateformat(endOfLast3Months, timezone)];
            break;
        }
        case THIS_YEAR_1W: {
            const date: Date = new Date();
            const currentDate = dayjs(date).tz(timezone);
            const startOfYear = currentDate.clone().startOf('year').toDate();
            formattedDateRange = [formatDate(startOfYear, timezone),formatDate(today.toDate(), timezone)];
            break;
        }
        case THIS_YEAR_1M: {
            const date: Date = new Date();
            const currentDate = dayjs(date).tz(timezone);
            const startOfYear = currentDate.clone().startOf('year').toDate();
            formattedDateRange = [formatDate(startOfYear, timezone),formatDate(today.toDate(), timezone)];
            break;
        }
        case LAST_YEAR_1M: {
            const startOfYear = today.clone().subtract(1, 'year').startOf('year').toDate();
            const endOfYear = today.clone().subtract(1, 'year').endOf('year').toDate();
            formattedDateRange = [formatDate(startOfYear, timezone),upperBoundDateformat(endOfYear, timezone)];
            break;
        }
        default:
            formattedDateRange = [];
            break;
        }
    return formattedDateRange;
};

